// Items, the pack (inventory grid + hotbar + armor slot) and recipes. Data-driven: tune here.

export type ItemKind = 'material' | 'tool' | 'weapon' | 'armor' | 'food' | 'place' | 'seed';
import type { BuffId } from './farming.ts';
import type { SigilId } from './blood.ts';
import type { SpellId } from './spells.ts';
import { cleanMagic, type Magic } from './loot.ts';

export type ItemDef = {
  name: string;
  kind: ItemKind;
  stack: number; // max per slot
  color: string; // fallback swatch
  /** Harvest power by tool type: power = tier (stone 1, bronze 2, iron 3). */
  axe?: number;
  pick?: number;
  damage?: number; // melee damage when held
  armor?: number; // flat damage reduction when worn
  heal?: number; // food
  buff?: BuffId; // food: a timed buff (BUFF_SECS)
  places?: number; // object id it becomes when placed (shared/content.ts OBJ)
  crop?: number; // seeds: index into CROPS
  hoe?: boolean; // tills soil inside your claim
  bucket?: 'empty' | 'full'; // fill at water, then water crops (3x3)
  sigil?: SigilId; // right-click to use (costs LP from your soul network)
  dagger?: boolean; // sacrificial dagger: right-click an altar
  tome?: SpellId; // right-click to learn the spell (the tome is used up)
  wand?: boolean; // basic attack is a missile at range (shared/spells.ts WAND)
  spell?: number; // % more spell damage and healing while held
  mana?: number; // more max mana while held
};

const mat = (name: string, color: string): ItemDef => ({ name, kind: 'material', stack: 99, color });

const RAW_ITEMS = {
  wood: mat('Wood', '#9a6b3a'),
  darkwood: mat('Bogwood', '#5a3f4a'),
  charwood: mat('Charwood', '#3a302c'),
  stone: mat('Stone', '#8c8c8c'),
  copper_ore: mat('Copper ore', '#c87a46'),
  tin_ore: mat('Tin ore', '#a9b8c0'),
  iron_ore: mat('Iron ore', '#8a6a5a'),
  coal: mat('Coal', '#2a2a2a'),
  silver_ore: mat('Silver ore', '#d8dde2'),
  gold_ore: mat('Gold ore', '#e8c040'),
  dark_ore: mat('Dark ore', '#7a2a5a'),
  obsidian: mat('Obsidian', '#2a1a3a'),
  hide: mat('Hide', '#8a5a3a'),
  charcoal: mat('Charcoal', '#3a3430'),
  bloodstone: mat('Bloodstone', '#a01828'),
  elite_heart: mat('Elite heart', '#c02838'),
  blank_slate: mat('Blank slate', '#8a8a90'),
  reinforced_slate: mat('Reinforced slate', '#6a5a6a'),
  imbued_slate: mat('Imbued slate', '#7a4a8a'),
  copper_dust: mat('Copper dust', '#d0884e'),
  tin_dust: mat('Tin dust', '#b8c4cc'),
  iron_dust: mat('Iron dust', '#9a7a6a'),
  silver_dust: mat('Silver dust', '#e0e4e8'),
  gold_dust: mat('Gold dust', '#f0c848'),
  copper_ingot: mat('Copper ingot', '#d0804a'),
  tin_ingot: mat('Tin ingot', '#c0ccd4'),
  iron_ingot: mat('Iron ingot', '#9a9ea4'),
  silver_ingot: mat('Silver ingot', '#e4e8ec'),
  gold_ingot: mat('Gold ingot', '#f0c840'),
  bronze_ingot: mat('Bronze ingot', '#c8904a'),
  steel_ingot: mat('Steel ingot', '#8a96a4'),
  electrum_ingot: mat('Electrum ingot', '#e8dc90'),
  dark_steel_ingot: mat('Dark steel ingot', '#5a4a6a'),
  // Boss trophies: one per dungeon, used in dark-steel gear.
  kings_signet: mat("The Barrow King's signet", '#d0b060'),
  bog_pearl: mat('Bog pearl', '#80c0a0'),
  minotaur_horn: mat('Minotaur horn', '#e0d0b0'),
  demon_heart: mat('Demon heart', '#e03020'),
  // Realm trophies (stage 8), and the keys that open the realms' portals (kept, not used up).
  lich_phylactery: mat("The Lich's phylactery", '#60e0a0'),
  dragon_scale: mat('Dragon scale', '#e04a2a'),
  hollow_key: { name: 'Key to the Hollow', kind: 'material', stack: 1, color: '#a0b0c0' },
  glade_key: { name: 'Key to the Twilight Glade', kind: 'material', stack: 1, color: '#6a7ae0' },
  bone: mat('Bone', '#e0d8c0'),
  parchment: mat('Parchment', '#e8d8a8'),
  arcane_ink: mat('Arcane ink', '#3a4aa0'),
  berries: { name: 'Berries', kind: 'food', stack: 99, color: '#c0304a', heal: 15 },
  meat: mat('Raw meat', '#c04a4a'),
  // Produce: edible raw, better cooked.
  wheat: mat('Wheat', '#e0c060'),
  carrot: { name: 'Carrot', kind: 'food', stack: 99, color: '#e08030', heal: 6 },
  onion: { name: 'Onion', kind: 'food', stack: 99, color: '#e8e0c8', heal: 4 },
  beet: { name: 'Beet', kind: 'food', stack: 99, color: '#a02848', heal: 6 },
  cranberry: { name: 'Cranberries', kind: 'food', stack: 99, color: '#c02838', heal: 5 },
  pumpkin: mat('Pumpkin', '#e08a20'),
  squash: { name: 'Squash', kind: 'food', stack: 99, color: '#7a9a3a', heal: 8 },
  emberberry: { name: 'Emberberries', kind: 'food', stack: 99, color: '#ff6a20', heal: 8 },
  wheat_seeds: { name: 'Wheat seeds', kind: 'seed', stack: 99, color: '#c0a050', crop: 0 },
  carrot_seeds: { name: 'Carrot seeds', kind: 'seed', stack: 99, color: '#c07030', crop: 1 },
  onion_seeds: { name: 'Onion seeds', kind: 'seed', stack: 99, color: '#c0b890', crop: 2 },
  beet_seeds: { name: 'Beet seeds', kind: 'seed', stack: 99, color: '#902040', crop: 3 },
  cranberry_seeds: { name: 'Cranberry seeds', kind: 'seed', stack: 99, color: '#a02030', crop: 4 },
  pumpkin_seeds: { name: 'Pumpkin seeds', kind: 'seed', stack: 99, color: '#d08020', crop: 5 },
  squash_seeds: { name: 'Squash seeds', kind: 'seed', stack: 99, color: '#6a8a30', crop: 6 },
  emberberry_seeds: { name: 'Emberberry seeds', kind: 'seed', stack: 99, color: '#e05a20', crop: 7 },
  // Meals: cooked at a cooking fire.
  bread: { name: 'Bread', kind: 'food', stack: 20, color: '#c08a40', heal: 20, buff: 'hearty' },
  garden_stew: { name: 'Garden stew', kind: 'food', stack: 20, color: '#8a6a3a', heal: 30, buff: 'regen' },
  roast: { name: 'Hearty roast', kind: 'food', stack: 20, color: '#a05a2a', heal: 40, buff: 'strong' },
  beet_soup: { name: 'Beet soup', kind: 'food', stack: 20, color: '#902848', heal: 30, buff: 'hearty' },
  cranberry_tart: { name: 'Cranberry tart', kind: 'food', stack: 20, color: '#c04050', heal: 25, buff: 'swift' },
  pumpkin_pie: { name: 'Pumpkin pie', kind: 'food', stack: 20, color: '#d08a30', heal: 30, buff: 'gatherer' },
  squash_soup: { name: 'Squash soup', kind: 'food', stack: 20, color: '#8aa040', heal: 35, buff: 'regen' },
  ember_chili: { name: 'Ember chili', kind: 'food', stack: 20, color: '#e04a20', heal: 45, buff: 'strong' },
  berry_juice: { name: 'Berry juice', kind: 'food', stack: 20, color: '#c03060', heal: 15, buff: 'swift' },
  fen_tea: { name: 'Fen tea', kind: 'food', stack: 20, color: '#6a8ac0', heal: 10, buff: 'clarity' },

  stone_axe: { name: 'Stone axe', kind: 'tool', stack: 1, color: '#8c8c8c', axe: 1, damage: 5 },
  stone_pick: { name: 'Stone pickaxe', kind: 'tool', stack: 1, color: '#8c8c8c', pick: 1, damage: 4 },
  wood_club: { name: 'Wooden club', kind: 'weapon', stack: 1, color: '#9a6b3a', damage: 7 },
  bronze_axe: { name: 'Bronze axe', kind: 'tool', stack: 1, color: '#c8904a', axe: 2, damage: 8 },
  bronze_pick: { name: 'Bronze pickaxe', kind: 'tool', stack: 1, color: '#c8904a', pick: 2, damage: 6 },
  bronze_sword: { name: 'Bronze sword', kind: 'weapon', stack: 1, color: '#c8904a', damage: 12 },
  // Ids say iron (old saves); since stage 4 they're steel, made from steel ingots.
  iron_axe: { name: 'Steel axe', kind: 'tool', stack: 1, color: '#9aa0a8', axe: 3, damage: 11 },
  iron_pick: { name: 'Steel pickaxe', kind: 'tool', stack: 1, color: '#9aa0a8', pick: 3, damage: 9 },
  iron_sword: { name: 'Steel sword', kind: 'weapon', stack: 1, color: '#9aa0a8', damage: 18 },
  hide_armor: { name: 'Hide armor', kind: 'armor', stack: 1, color: '#8a5a3a', armor: 2 },
  bronze_armor: { name: 'Bronze armor', kind: 'armor', stack: 1, color: '#c8904a', armor: 4 },
  iron_armor: { name: 'Steel armor', kind: 'armor', stack: 1, color: '#9aa0a8', armor: 7 },
  electrum_axe: { name: 'Electrum axe', kind: 'tool', stack: 1, color: '#e8dc90', axe: 4, damage: 14 },
  electrum_pick: { name: 'Electrum pickaxe', kind: 'tool', stack: 1, color: '#e8dc90', pick: 4, damage: 12 },
  electrum_sword: { name: 'Electrum sword', kind: 'weapon', stack: 1, color: '#e8dc90', damage: 24 },
  electrum_armor: { name: 'Electrum armor', kind: 'armor', stack: 1, color: '#e8dc90', armor: 10 },
  dark_steel_axe: { name: 'Dark steel axe', kind: 'tool', stack: 1, color: '#5a4a6a', axe: 5, damage: 18 },
  dark_steel_pick: { name: 'Dark steel pickaxe', kind: 'tool', stack: 1, color: '#5a4a6a', pick: 5, damage: 15 },
  dark_steel_sword: { name: 'Dark steel sword', kind: 'weapon', stack: 1, color: '#5a4a6a', damage: 32 },
  dark_steel_armor: { name: 'Dark steel armor', kind: 'armor', stack: 1, color: '#5a4a6a', armor: 14 },

  // Combat magic (shared/spells.ts): tomes teach a spell; wands and staves boost spells.
  tome_arcane_bolt: { name: 'Tome: Arcane Bolt', kind: 'material', stack: 1, color: '#4a6ad0', tome: 'arcane_bolt' },
  tome_renew: { name: 'Tome: Renew', kind: 'material', stack: 1, color: '#4ab06a', tome: 'renew' },
  tome_ward: { name: 'Tome: Ward', kind: 'material', stack: 1, color: '#c0c0e0', tome: 'ward' },
  tome_fire_nova: { name: 'Tome: Fire Nova', kind: 'material', stack: 1, color: '#e07030', tome: 'fire_nova' },
  tome_meteor: { name: 'Tome: Meteor', kind: 'material', stack: 1, color: '#e0a030', tome: 'meteor' },
  tome_blood_lance: { name: 'Tome: Blood Lance', kind: 'material', stack: 1, color: '#c02030', tome: 'blood_lance' },
  tome_soul_siphon: { name: 'Tome: Soul Siphon', kind: 'material', stack: 1, color: '#60e0a0', tome: 'soul_siphon' },
  tome_dragonfire: { name: 'Tome: Dragonfire', kind: 'material', stack: 1, color: '#ff6a2a', tome: 'dragonfire' },
  apprentice_wand: { name: 'Apprentice wand', kind: 'weapon', stack: 1, color: '#9a6b3a', damage: 6, wand: true, spell: 10, mana: 10 },
  silver_staff: { name: 'Silver staff', kind: 'weapon', stack: 1, color: '#d8dde2', damage: 11, wand: true, spell: 25, mana: 25 },
  electrum_staff: { name: 'Electrum staff', kind: 'weapon', stack: 1, color: '#e8dc90', damage: 16, wand: true, spell: 45, mana: 45 },

  // Movable affixes: lift one off an item into a soul gem at an enchanter's bench, set it in another.
  enchant_bench: { name: "Enchanter's bench", kind: 'place', stack: 5, color: '#6a4a8a', places: 81 },
  soul_gem: mat('Soul gem', '#c04070'),
  inscribed_gem: { name: 'Inscribed gem', kind: 'material', stack: 1, color: '#7a5ae0' },

  // Renewable power (shared/machines.ts): resin and oil, and the free-but-weak wheel and windmill.
  resin: mat('Resin', '#c09030'),
  sapling: { name: 'Sapling', kind: 'place', stack: 20, color: '#5a8a3a', places: 88 },
  tree_oil: mat('Tree oil', '#a07a20'),
  plant_oil: mat('Plant oil', '#d0b040'),
  resin_tap: { name: 'Resin tap', kind: 'place', stack: 20, color: '#8a6a3a', places: 83 },
  still: { name: 'Still', kind: 'place', stack: 5, color: '#c08a40', places: 82 },
  harvester: { name: 'Harvester', kind: 'place', stack: 5, color: '#a08040', places: 84 },
  water_wheel: { name: 'Water wheel', kind: 'place', stack: 5, color: '#8a6a40', places: 85 },
  windmill: { name: 'Windmill', kind: 'place', stack: 5, color: '#8a6a40', places: 86 },
  alternator: { name: 'Alternator', kind: 'place', stack: 5, color: '#6a6a7a', places: 87 },

  claim_banner: { name: 'Claim banner', kind: 'place', stack: 5, color: '#3a5aa0', places: 29 },
  stone_wall: { name: 'Stone wall', kind: 'place', stack: 99, color: '#9a9488', places: 30 },
  chest: { name: 'Chest', kind: 'place', stack: 10, color: '#8a5a2a', places: 31 },
  torch: { name: 'Torch', kind: 'place', stack: 99, color: '#f0a040', places: 32 },
  cooking_fire: { name: 'Cooking fire', kind: 'place', stack: 5, color: '#e07030', places: 34 },
  stone_furnace: { name: 'Stone furnace', kind: 'place', stack: 5, color: '#8a6a5a', places: 35 },
  generator: { name: 'Generator', kind: 'place', stack: 5, color: '#8a7a5a', places: 36 },
  sag_mill: { name: 'SAG mill', kind: 'place', stack: 5, color: '#7a7a8a', places: 37 },
  alloy_smelter: { name: 'Alloy smelter', kind: 'place', stack: 5, color: '#c05a2a', places: 38 },
  conduit: { name: 'Item Pipe', kind: 'place', stack: 99, color: '#c8804a', places: 39 },
  cable: { name: 'Energy Cable', kind: 'place', stack: 99, color: '#5a6a7a', places: 80 },
  storage_terminal: { name: 'Storage terminal', kind: 'place', stack: 5, color: '#4a8ac0', places: 77 },
  storage_core: { name: 'Storage core', kind: 'place', stack: 1, color: '#4a7ac0', places: 76 },
  storage_drive: { name: 'Storage drive', kind: 'material', stack: 4, color: '#6a9ad0' },
  large_storage_drive: { name: 'Large storage drive', kind: 'material', stack: 4, color: '#9a7ad0' },
  sacrificial_dagger: { name: 'Sacrificial dagger', kind: 'tool', stack: 1, color: '#a02030', dagger: true, damage: 4 },
  weak_blood_orb: { name: 'Weak blood orb', kind: 'tool', stack: 1, color: '#c02040' },
  apprentice_blood_orb: { name: 'Apprentice blood orb', kind: 'tool', stack: 1, color: '#d03060' },
  magician_blood_orb: { name: "Magician's blood orb", kind: 'tool', stack: 1, color: '#e04070' },
  blood_capstone: { name: 'Blood capstone', kind: 'place', stack: 4, color: '#a02030', places: 89 },
  focus_grove: { name: 'Focus of the Grove', kind: 'material', stack: 4, color: '#6ac04a' },
  focus_warding: { name: 'Focus of Warding', kind: 'material', stack: 4, color: '#80b0ff' },
  focus_suffering: { name: 'Focus of Suffering', kind: 'material', stack: 4, color: '#e02030' },
  sigil_blood_bolt: { name: 'Sigil of the Blood Bolt', kind: 'tool', stack: 1, color: '#c02030', sigil: 'blood_bolt' },
  sigil_mending: { name: 'Sigil of Mending', kind: 'tool', stack: 1, color: '#4ac06a', sigil: 'mending' },
  sigil_swiftness: { name: 'Sigil of Swiftness', kind: 'tool', stack: 1, color: '#4a8ac0', sigil: 'swiftness' },
  sigil_growth: { name: 'Sigil of Growth', kind: 'tool', stack: 1, color: '#7ac04a', sigil: 'growth' },
  sigil_blood_lamp: { name: 'Sigil of the Blood Lamp', kind: 'tool', stack: 1, color: '#e04040', sigil: 'blood_lamp' },
  blood_altar: { name: 'Blood altar', kind: 'place', stack: 1, color: '#801828', places: 72 },
  blood_rune: { name: 'Blood rune', kind: 'place', stack: 16, color: '#902030', places: 73 },
  master_ritual_stone: { name: 'Master ritual stone', kind: 'place', stack: 1, color: '#5a2a3a', places: 74 },
  stone_hoe: { name: 'Stone hoe', kind: 'tool', stack: 1, color: '#8c8c8c', hoe: true, damage: 3 },
  bucket: { name: 'Bucket', kind: 'tool', stack: 1, color: '#8a6a3a', bucket: 'empty' },
  water_bucket: { name: 'Water bucket', kind: 'tool', stack: 1, color: '#4a7aaa', bucket: 'full' },
} satisfies Record<string, ItemDef>;

export type ItemId = keyof typeof RAW_ITEMS;
export const ITEMS: Record<ItemId, ItemDef> = RAW_ITEMS;
export const isItemId = (v: unknown): v is ItemId => typeof v === 'string' && Object.hasOwn(ITEMS, v);
export const itemDef = (id: ItemId): ItemDef => ITEMS[id];

// ---------------------------------------------------------------- recipes
// No crafting stations yet (stage 4 brings machines and smelting): ore goes straight into gear.

/** station: an object that must be within 3 tiles (e.g. the cooking fire). */
export type Station = 'cooking_fire' | 'furnace';
export type Recipe = { id: ItemId; n: number; needs: [ItemId, number][]; station?: Station };

export const RECIPES: Recipe[] = [
  { id: 'stone_axe', n: 1, needs: [['wood', 3], ['stone', 2]] },
  { id: 'stone_pick', n: 1, needs: [['wood', 3], ['stone', 3]] },
  { id: 'wood_club', n: 1, needs: [['wood', 5]] },
  { id: 'torch', n: 2, needs: [['wood', 2]] },
  { id: 'claim_banner', n: 1, needs: [['wood', 6], ['stone', 4]] },
  { id: 'stone_wall', n: 1, needs: [['stone', 3]] },
  { id: 'chest', n: 1, needs: [['wood', 8]] },
  { id: 'hide_armor', n: 1, needs: [['hide', 6]] },
  { id: 'parchment', n: 2, needs: [['hide', 1]] },
  { id: 'enchant_bench', n: 1, needs: [['wood', 8], ['stone', 6], ['bronze_ingot', 2], ['parchment', 2]] },
  { id: 'soul_gem', n: 1, needs: [['bloodstone', 1], ['bronze_ingot', 1]] },
  { id: 'arcane_ink', n: 2, needs: [['charcoal', 1], ['copper_dust', 1]] },
  { id: 'tome_arcane_bolt', n: 1, needs: [['parchment', 3], ['arcane_ink', 1]] },
  { id: 'tome_renew', n: 1, needs: [['parchment', 3], ['arcane_ink', 1], ['berries', 4]] },
  { id: 'tome_ward', n: 1, needs: [['parchment', 4], ['arcane_ink', 2], ['silver_ingot', 1]] },
  { id: 'apprentice_wand', n: 1, needs: [['wood', 3], ['copper_ingot', 2], ['arcane_ink', 1]] },
  { id: 'silver_staff', n: 1, needs: [['darkwood', 4], ['silver_ingot', 3], ['arcane_ink', 2]] },
  { id: 'electrum_staff', n: 1, needs: [['charwood', 4], ['electrum_ingot', 3], ['elite_heart', 1], ['arcane_ink', 2]] },
  // Machines (stage 4). Everything here can be made from Greenwood metal.
  { id: 'stone_furnace', n: 1, needs: [['stone', 8]] },
  { id: 'conduit', n: 4, needs: [['copper_ingot', 1], ['stone', 1]] },
  { id: 'cable', n: 4, needs: [['copper_ingot', 1], ['wood', 1]] },
  { id: 'resin_tap', n: 1, needs: [['wood', 2], ['copper_ingot', 1]] },
  { id: 'still', n: 1, needs: [['copper_ingot', 6], ['bronze_ingot', 2], ['stone', 6]] },
  { id: 'harvester', n: 1, needs: [['steel_ingot', 4], ['bronze_ingot', 2], ['wood', 6]] },
  { id: 'water_wheel', n: 1, needs: [['wood', 12], ['bronze_ingot', 2]] },
  { id: 'windmill', n: 1, needs: [['wood', 10], ['hide', 4], ['bronze_ingot', 2]] },
  { id: 'alternator', n: 1, needs: [['copper_ingot', 6], ['steel_ingot', 2]] },
  { id: 'generator', n: 1, needs: [['stone', 10], ['copper_ingot', 4]] },
  { id: 'sag_mill', n: 1, needs: [['stone', 12], ['copper_ingot', 4], ['tin_ingot', 2]] },
  { id: 'alloy_smelter', n: 1, needs: [['stone', 10], ['copper_ingot', 6], ['tin_ingot', 2]] },
  // Storage (stage 6). A terminal works with plain chests; the core and its drives are Highlands tech.
  { id: 'storage_terminal', n: 1, needs: [['stone', 4], ['bronze_ingot', 2], ['copper_ingot', 2]] },
  { id: 'storage_core', n: 1, needs: [['stone', 10], ['steel_ingot', 4], ['silver_ingot', 2]] },
  { id: 'storage_drive', n: 1, needs: [['silver_ingot', 1], ['bronze_ingot', 2]] },
  { id: 'large_storage_drive', n: 1, needs: [['electrum_ingot', 2], ['steel_ingot', 2]] },
  // Alloys by hand at a furnace: works, but the alloy smelter makes far more from the same metal.
  { id: 'bronze_ingot', n: 1, needs: [['copper_ingot', 3], ['tin_ingot', 1]], station: 'furnace' },
  { id: 'steel_ingot', n: 1, needs: [['iron_ingot', 2], ['coal', 2]], station: 'furnace' },
  { id: 'electrum_ingot', n: 1, needs: [['silver_ingot', 1], ['gold_ingot', 1]], station: 'furnace' },
  // Metal gear.
  { id: 'bronze_axe', n: 1, needs: [['wood', 2], ['bronze_ingot', 3]] },
  { id: 'bronze_pick', n: 1, needs: [['wood', 2], ['bronze_ingot', 3]] },
  { id: 'bronze_sword', n: 1, needs: [['wood', 1], ['bronze_ingot', 4]] },
  { id: 'bronze_armor', n: 1, needs: [['hide', 2], ['bronze_ingot', 8]] },
  { id: 'iron_axe', n: 1, needs: [['wood', 2], ['steel_ingot', 3]] },
  { id: 'iron_pick', n: 1, needs: [['wood', 2], ['steel_ingot', 3]] },
  { id: 'iron_sword', n: 1, needs: [['wood', 1], ['steel_ingot', 4]] },
  { id: 'iron_armor', n: 1, needs: [['hide', 2], ['steel_ingot', 8]] },
  { id: 'electrum_axe', n: 1, needs: [['charwood', 2], ['electrum_ingot', 3]] },
  { id: 'electrum_pick', n: 1, needs: [['charwood', 2], ['electrum_ingot', 3]] },
  { id: 'electrum_sword', n: 1, needs: [['charwood', 1], ['electrum_ingot', 4]] },
  { id: 'electrum_armor', n: 1, needs: [['hide', 3], ['electrum_ingot', 8]] },
  // Dark steel (Ashlands): each piece needs a trophy from a dungeon boss.
  { id: 'dark_steel_axe', n: 1, needs: [['charwood', 2], ['dark_steel_ingot', 3], ['minotaur_horn', 1]] },
  { id: 'dark_steel_pick', n: 1, needs: [['charwood', 2], ['dark_steel_ingot', 3], ['minotaur_horn', 1]] },
  { id: 'dark_steel_sword', n: 1, needs: [['charwood', 1], ['dark_steel_ingot', 4], ['demon_heart', 1]] },
  { id: 'dark_steel_armor', n: 1, needs: [['hide', 3], ['dark_steel_ingot', 8], ['kings_signet', 1], ['bog_pearl', 1]] },
  { id: 'dark_steel_ingot', n: 1, needs: [['dark_ore', 2], ['obsidian', 1]], station: 'furnace' },
  // Realm keys (stage 8): open a realm's portal while in your pack. Each needs a dungeon boss's trophy.
  { id: 'hollow_key', n: 1, needs: [['minotaur_horn', 1], ['bone', 10], ['arcane_ink', 2], ['silver_ingot', 2]] },
  { id: 'glade_key', n: 1, needs: [['demon_heart', 1], ['darkwood', 10], ['electrum_ingot', 2], ['arcane_ink', 2]] },
  // Blood Magic (stage 5). Slates and orbs come out of the altar, not the pack.
  { id: 'sacrificial_dagger', n: 1, needs: [['wood', 1], ['stone', 2], ['bone', 1]] },
  { id: 'blood_altar', n: 1, needs: [['stone', 12], ['bloodstone', 1], ['bone', 4]] },
  { id: 'blood_rune', n: 1, needs: [['blank_slate', 1], ['stone', 4]] },
  { id: 'sigil_blood_bolt', n: 1, needs: [['blank_slate', 1], ['bloodstone', 1], ['bone', 2]] },
  { id: 'sigil_mending', n: 1, needs: [['blank_slate', 1], ['berries', 6], ['bone', 1]] },
  { id: 'sigil_swiftness', n: 1, needs: [['blank_slate', 1], ['hide', 3]] },
  { id: 'sigil_growth', n: 1, needs: [['blank_slate', 1], ['wheat', 4], ['carrot', 2]] },
  { id: 'sigil_blood_lamp', n: 1, needs: [['blank_slate', 1], ['torch', 4]] },
  { id: 'master_ritual_stone', n: 1, needs: [['reinforced_slate', 2], ['stone', 10], ['bloodstone', 2]] },
  // Blood Magic tier 3 (stage 8): capstones make a tier-3 altar; foci switch a ritual stone's ritual.
  { id: 'blood_capstone', n: 1, needs: [['reinforced_slate', 2], ['bloodstone', 3], ['elite_heart', 1], ['obsidian', 2]] },
  { id: 'focus_grove', n: 1, needs: [['blank_slate', 1], ['wheat', 6], ['sapling', 2]] },
  { id: 'focus_warding', n: 1, needs: [['imbued_slate', 2], ['kings_signet', 1], ['bog_pearl', 1]] },
  { id: 'focus_suffering', n: 1, needs: [['imbued_slate', 2], ['demon_heart', 1], ['bone', 10]] },
  { id: 'stone_hoe', n: 1, needs: [['wood', 2], ['stone', 2]] },
  { id: 'bucket', n: 1, needs: [['wood', 4]] },
  { id: 'cooking_fire', n: 1, needs: [['stone', 6], ['wood', 4]] },
  { id: 'bread', n: 1, needs: [['wheat', 3]], station: 'cooking_fire' },
  { id: 'fen_tea', n: 1, needs: [['cranberry', 2], ['onion', 1]], station: 'cooking_fire' },
  { id: 'garden_stew', n: 1, needs: [['carrot', 2], ['onion', 1]], station: 'cooking_fire' },
  { id: 'roast', n: 1, needs: [['meat', 2], ['onion', 1]], station: 'cooking_fire' },
  { id: 'berry_juice', n: 1, needs: [['berries', 4]], station: 'cooking_fire' },
  { id: 'beet_soup', n: 1, needs: [['beet', 2], ['onion', 1]], station: 'cooking_fire' },
  { id: 'cranberry_tart', n: 1, needs: [['cranberry', 4], ['wheat', 2]], station: 'cooking_fire' },
  { id: 'pumpkin_pie', n: 1, needs: [['pumpkin', 1], ['wheat', 2], ['berries', 2]], station: 'cooking_fire' },
  { id: 'squash_soup', n: 1, needs: [['squash', 2], ['carrot', 1]], station: 'cooking_fire' },
  { id: 'ember_chili', n: 1, needs: [['emberberry', 3], ['meat', 1], ['onion', 1]], station: 'cooking_fire' },
];

// ---------------------------------------------------------------- the pack

/** m: a magic item instance (rarity + affixes, shared/loot.ts); only on stack-of-1 gear. */
export type Slot = { id: ItemId; n: number; m?: Magic } | null;
export const PACK_SIZE = 32; // 4 rows of 8; row 0 is the hotbar
export const HOTBAR = 8;
export const CHEST_SIZE = 16;

export type Pack = { slots: Slot[]; armor: Slot; sel: number };

export const emptyPack = (): Pack => ({ slots: Array(PACK_SIZE).fill(null), armor: null, sel: 0 });

export function cleanSlot(v: unknown): Slot {
  if (!v || typeof v !== 'object') return null;
  const s = v as { id?: unknown; n?: unknown };
  if (!isItemId(s.id) || typeof s.n !== 'number' || s.n < 1) return null;
  const m = ITEMS[s.id].stack === 1 ? cleanMagic((v as { m?: unknown }).m) : undefined;
  return m ? { id: s.id, n: 1, m } : { id: s.id, n: Math.min(ITEMS[s.id].stack, Math.floor(s.n)) };
}

export function cleanSlots(v: unknown, size: number): Slot[] {
  const arr = Array.isArray(v) ? v : [];
  return Array.from({ length: size }, (_, i) => cleanSlot(arr[i]));
}

export function count(slots: Slot[], id: ItemId): number {
  return slots.reduce((t, s) => t + (s?.id === id ? s.n : 0), 0);
}

/** Adds as much as fits; returns how many didn't fit. Hotbar first for tools, stacks first for everything. */
export function addItem(slots: Slot[], id: ItemId, n: number): number {
  const max = ITEMS[id].stack;
  for (const s of slots) {
    if (n <= 0) break;
    if (s && s.id === id && s.n < max) {
      const k = Math.min(n, max - s.n);
      s.n += k;
      n -= k;
    }
  }
  for (let i = 0; i < slots.length && n > 0; i++) {
    if (slots[i]) continue;
    const k = Math.min(n, max);
    slots[i] = { id, n: k };
    n -= k;
  }
  return n;
}

/** Adds a whole slot, keeping a magic item's affixes. Returns how many didn't fit. */
export function addSlot(slots: Slot[], s: NonNullable<Slot>): number {
  if (!s.m) return addItem(slots, s.id, s.n);
  const i = slots.findIndex((x) => !x);
  if (i < 0) return s.n;
  slots[i] = { ...s };
  return 0;
}

/** Removes n (caller checked count). Takes from the back of the pack first so the hotbar keeps its stack. */
export function removeItem(slots: Slot[], id: ItemId, n: number): void {
  // Plain items first, so a recipe never eats your magic gear while a plain one would do.
  for (const magic of [false, true]) {
    for (let i = slots.length - 1; i >= 0 && n > 0; i--) {
      const s = slots[i];
      if (!s || s.id !== id || Boolean(s.m) !== magic) continue;
      const k = Math.min(n, s.n);
      s.n -= k;
      n -= k;
      if (s.n === 0) slots[i] = null;
    }
  }
}
